// src/api/api.js
import axios from "axios";

// Production:

export const BASE_URL = "https://zionpraiseacademy.online";


// Development:
// export const BASE_URL = "http://127.0.0.1:8000";

const api = axios.create({
  baseURL: `${BASE_URL}`,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

let isRefreshing = false;
let requestQueue = [];
// Clear tokens and trigger logout event
const handleLogout = () => {
  console.warn("[Logout] Clearing tokens and forcing logout.");
  localStorage.removeItem("access");
  localStorage.removeItem("refresh");
  window.dispatchEvent(new Event("force-logout"));
};
// Resolve or reject queued requests //
const processQueue = (error, newAccessToken = null) => {
  requestQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(newAccessToken);
    }
  });

  requestQueue = [];
};

// Attach JWT access token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("access");

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Handle token expiration
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config;

    if (!originalRequest || !error.response) {
      return Promise.reject(error);
    }

    if (error.response.status === 401 && !originalRequest._retry) {
      const refreshToken = localStorage.getItem("refresh");

      if (!refreshToken) {
        console.error("[Response] No refresh token. Logging out.");
        handleLogout();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          requestQueue.push({
            resolve: (token) => {
              if (token) {
                originalRequest.headers.Authorization = `Bearer ${token}`;
              }

              resolve(api(originalRequest));
            },
            reject,
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshClient = axios.create({
          baseURL: BASE_URL,
          headers: {
            "Content-Type": "application/json",
          },
        });

        const res = await refreshClient.post("/api/token/refresh/", {
          refresh: refreshToken,
        });

        const newAccessToken = res.data?.access;

        if (!newAccessToken) {
          throw new Error("No access token returned from refresh.");
        }

        localStorage.setItem("access", newAccessToken);

        api.defaults.headers.Authorization = `Bearer ${newAccessToken}`;

        console.log("[Token Refresh] New access token acquired.");

        processQueue(null, newAccessToken);

        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

        return api(originalRequest);

      } catch (refreshError) {
        console.error("[Token Refresh] Failed:", refreshError);

        processQueue(refreshError, null);

        handleLogout();

        return Promise.reject(refreshError);

      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;

